/** @jsxImportSource theme-ui */
import React, { useState, useEffect } from "react"
import _ from "lodash"
import { useTranslation } from "react-i18next"
import { useMutation } from "react-relay"
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faGripVertical } from "@fortawesome/free-solid-svg-icons"
import { Flex } from "theme-ui"

import BoxButton from "../../Radiate/NewBoxButton/NewBoxButton"
import { toast } from "../../Radiate/Toaster/Toaster"
import { SetChannelOutboundHooksMutation } from "../SettingsModalQuery"
import { isConflict, errorMessage, networkErrorMessage } from "./hookErrors"
import * as Styles from "./styles"

const ChannelHooksPanel = ({ channel, appHooks = [], canEdit }) => {
  const { t } = useTranslation(["settings", "common"])
  const [setHooks, saving] = useMutation(SetChannelOutboundHooksMutation)

  const toItem = (h) => ({ _id: h._id, id: h.id, name: h.name })
  const serverAttached = (channel?.outboundHooks || []).map(toItem)
  const [attached, setAttached] = useState(serverAttached)
  const [etag, setEtag] = useState(channel?.etag)
  const [conflict, setConflict] = useState(false)
  const [error, setError] = useState("")

  // Re-seed the local edit buffer when the underlying channel record changes —
  // on refetch, or after a save, since setChannelOutboundHooks returns the same
  // Relay id as this list node so its payload merges into this record in place.
  useEffect(() => {
    setAttached((channel?.outboundHooks || []).map(toItem))
    setEtag(channel?.etag)
    // eslint-disable-next-line
  }, [channel?.etag, (channel?.outboundHooks || []).map((h) => h._id).join(",")])

  const libraryByDbId = _.keyBy(appHooks, "_id")
  // Raw string _ids — the contract setChannelOutboundHooks expects.
  const attachedDbIds = attached.map((h) => h._id)
  const available = appHooks.filter((h) => !attachedDbIds.includes(h._id))

  const dirty = !_.isEqual(attachedDbIds, serverAttached.map((h) => h._id))

  const onDragEnd = (result) => {
    if (!result.destination) return
    const items = Array.from(attached)
    const [moved] = items.splice(result.source.index, 1)
    items.splice(result.destination.index, 0, moved)
    setAttached(items)
  }

  const addHook = (hook) => setAttached([...attached, toItem(hook)])
  const removeHook = (dbId) => setAttached(attached.filter((h) => h._id !== dbId))

  const handleSave = () => {
    setError("")
    let clientMutationId = 0
    setHooks({
      variables: {
        input: {
          wChannelId: channel.wChannelId,
          hookIds: attachedDbIds,
          etag,
          clientMutationId: clientMutationId++,
        },
      },
      onCompleted: (response) => {
        const payload = response?.setChannelOutboundHooks
        const err = payload?.error
        if (err) {
          if (isConflict(err)) {
            setConflict(true)
            return
          }
          setError(errorMessage(err, t("common:errors.something_went_wrong", "Something went wrong.")))
          return
        }
        const updated = payload?.channel
        if (updated) {
          setAttached((updated.outboundHooks || []).map(toItem))
          setEtag(updated.etag)
        }
        setConflict(false)
        toast.success(t("outbound_hooks.channel.saved", "Channel hooks saved"))
      },
      onError: (e) => {
        setError(networkErrorMessage(e, t("common:errors.something_went_wrong", "Something went wrong.")))
      },
    })
  }

  return (
    <Styles.ChannelPanel>
      {conflict && (
        <Styles.ConflictBanner>
          <span>
            {t(
              "outbound_hooks.channel.conflict",
              "This channel was changed somewhere else. The list below has been refreshed — review and save again."
            )}
          </span>
        </Styles.ConflictBanner>
      )}

      {attached.length === 0 ? (
        <Styles.EmptyState>
          {t("outbound_hooks.channel.empty", "No hooks attached. Outbound messages send normally.")}
        </Styles.EmptyState>
      ) : (
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId={`channel-hooks-${channel?.wChannelId}`}>
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps}>
                {attached.map((hook, index) => {
                  const isBroken = !libraryByDbId[hook._id]
                  return (
                    <Draggable
                      key={hook._id}
                      draggableId={String(hook._id)}
                      index={index}
                      isDragDisabled={!canEdit}
                    >
                      {(prov, snapshot) => (
                        <Styles.DragItem
                          ref={prov.innerRef}
                          {...prov.draggableProps}
                          isDragging={snapshot.isDragging}
                          isBroken={isBroken}
                          style={{ marginBottom: 6, ...prov.draggableProps.style }}
                        >
                          {canEdit && (
                            <Styles.DragHandle {...prov.dragHandleProps}>
                              <FontAwesomeIcon icon={faGripVertical} />
                            </Styles.DragHandle>
                          )}
                          <Styles.DragItemMain>
                            {hook.name ||
                              (isBroken
                                ? t("outbound_hooks.channel.broken_ref", "Broken reference")
                                : hook._id)}
                            {isBroken && (
                              <span sx={{ color: "dangerText", ml: 2, fontSize: "11px" }}>
                                {t("outbound_hooks.channel.broken_tag", "(missing — will be skipped)")}
                              </span>
                            )}
                          </Styles.DragItemMain>
                          {canEdit && (
                            <FontAwesomeIcon
                              icon={faTimes}
                              sx={{ cursor: "pointer", color: "gray5" }}
                              onClick={() => removeHook(hook._id)}
                            />
                          )}
                        </Styles.DragItem>
                      )}
                    </Draggable>
                  )
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
      )}

      {canEdit && available.length > 0 && (
        <div>
          <div sx={{ fontSize: "12px", color: "gray6", mt: 2, mb: 1 }}>
            {t("outbound_hooks.channel.add", "Add a hook")}
          </div>
          <Flex sx={{ flexWrap: "wrap", gap: 2 }}>
            {available.map((hook) => (
              <Styles.HostChip
                key={hook.id}
                sx={{ cursor: "pointer" }}
                onClick={() => addHook(hook)}
              >
                <FontAwesomeIcon icon="plus" sx={{ fontSize: "10px" }} />
                <span>{hook.name}</span>
              </Styles.HostChip>
            ))}
          </Flex>
        </div>
      )}

      {error && <Styles.InlineError>{error}</Styles.InlineError>}

      {canEdit && (
        <Styles.PanelActions>
          <BoxButton
            primary
            text={t("common:buttons.save", "Save")}
            loading={saving}
            disabled={!dirty || saving}
            onClick={handleSave}
          />
        </Styles.PanelActions>
      )}
    </Styles.ChannelPanel>
  )
}

export default ChannelHooksPanel
